import { FC, useCallback, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { cx } from '@linaria/core';
import { IconPause, IconFullscreenToggle, IconPlay, IconPlayNext, IconPlayPrev, IconSkipPrev, IconSkipNext } from 'components/Icons';
import { Button } from 'components/deprecated/Button';
import { Slider } from 'components/Slider';
import { useMediaState } from 'modules/Canvas/contexts/MediaProvider/MediaState';
import { useMediaPlayback } from 'modules/Canvas/contexts/MediaProvider/MediaPlayback';
import { noop } from 'utils/fp';
import { baseTheme } from 'styles/utils/core';
import { useVideoTrackTimelineStore } from 'modules/VideoTrackTimeline/contexts/StoreProvider';

import { VideoInfo } from 'clarifai-web-grpc/proto/clarifai/api/resources_pb';
import { formatTime, extractVideoMetadata } from 'modules/Canvas/components/VideoControls/helpers';
import { buttonGroup, buttons, timelineWrapper, timing, videoControlsWrapper } from 'modules/Canvas/components/VideoControls/VideoControls.styles';
import { useVideoControllers } from 'modules/Canvas/components/VideoControls/useVideoControllers';

const FRAME_STEP = 1;
interface VideoControlsProps {
  className?: string;
  currentFrame: number;
  showFullScreenButton?: boolean;
  videoElement: HTMLVideoElement;
  videoMetadata: VideoInfo | CF.API.Metadata | undefined;
  onCurrentFrameChanged: (frame: number, fps: number) => void;
  onFullScreenToggle: () => void;
}

export const VideoControls_: FC<VideoControlsProps> = observer(
  ({ className, videoElement, currentFrame, showFullScreenButton = true, videoMetadata, onCurrentFrameChanged, onFullScreenToggle }) => {
    const { fps, framesCount } = extractVideoMetadata(videoMetadata);
    const videoTrackTimelineStore = useVideoTrackTimelineStore();

    const { isPlaying, onPause, onPlay, changeMute, prevFrame, nextFrame, pauseWithAdjust, changeCurrentFrame } = useVideoControllers({
      currentFrame,
      onCurrentFrameChanged,
      videoElement,
      store: videoTrackTimelineStore,
      fps,
      framesCount,
    });

    const onPlayToggle = useCallback(() => {
      if (isPlaying) onPause();
      else onPlay();
    }, [onPause, onPlay, isPlaying]);

    const onNextClick = useCallback(() => nextFrame(1), [nextFrame]);
    const onPrevClick = useCallback(() => prevFrame(1), [prevFrame]);
    const onFirstFrameClick = useCallback(() => changeCurrentFrame(0), [changeCurrentFrame]);
    const onLastFrameClick = useCallback(() => changeCurrentFrame(framesCount), [changeCurrentFrame, framesCount]);
    const onRangeChange = useCallback(
      (frame: number) => {
        pauseWithAdjust(frame);
      },
      [pauseWithAdjust],
    );

    useEffect(() => {
      changeMute(true);
    }, [videoElement, changeMute]);

    return (
      <div className={cx(videoControlsWrapper, className)}>
        <div className={buttonGroup}>
          <Button className={buttons} onClick={onFirstFrameClick}>
            <IconSkipPrev size={20} color={baseTheme.light.colors.gray500} />
          </Button>

          <Button className={buttons} onClick={onPrevClick} data-testid="play-prev-button">
            <IconPlayPrev size={20} color={baseTheme.light.colors.gray500} />
          </Button>

          <Button className={buttons} onClick={onPlayToggle} data-testid="play-button">
            {isPlaying ? (
              <IconPause size={20} data-testid="pause-icon" color={baseTheme.light.colors.gray500} />
            ) : (
              <IconPlay size={20} data-testid="play-icon" color={baseTheme.light.colors.gray500} />
            )}
          </Button>

          <Button className={buttons} onClick={onNextClick} data-testid="play-next-button">
            <IconPlayNext size={20} color={baseTheme.light.colors.gray500} />
          </Button>

          <Button className={buttons} onClick={onLastFrameClick}>
            <IconSkipNext size={20} color={baseTheme.light.colors.gray500} />
          </Button>
        </div>

        <div className={timing}>
          <span data-testid="current-frame">{Math.round(currentFrame)}</span> / {framesCount}
        </div>

        <Slider min={0} max={framesCount} step={FRAME_STEP} onChange={onRangeChange} className={timelineWrapper} value={currentFrame} />

        <div className={timing}>
          <span data-testid="current-time">{formatTime(currentFrame / fps)}</span> / {formatTime(framesCount / fps)}
        </div>

        {showFullScreenButton && (
          <Button className={cx(buttons, 'fullscreen-toggle')} onClick={onFullScreenToggle}>
            <IconFullscreenToggle size={18} color={baseTheme.light.colors.gray500} />
          </Button>
        )}
      </div>
    );
  },
);

export const VideoControls: FC<Partial<VideoControlsProps>> = observer(
  ({ className, showFullScreenButton, videoMetadata, onFullScreenToggle = noop, onCurrentFrameChanged }) => {
    const mediaState = useMediaState();
    const mediaPlayback = useMediaPlayback();
    const videoElement = mediaState.element;
    const { fps } = extractVideoMetadata(videoMetadata);
    mediaPlayback.setFps(fps);
    const handleCurrentTimeChange = useCallback(
      (frame: number = 0): void => {
        mediaPlayback.setCurrentFrame(frame);
        onCurrentFrameChanged?.(frame, fps);
      },
      [onCurrentFrameChanged, mediaPlayback, fps],
    );

    return !videoElement || !(videoElement instanceof HTMLVideoElement) ? null : (
      <VideoControls_
        className={className}
        showFullScreenButton={showFullScreenButton}
        currentFrame={mediaPlayback.currentFrame || 0}
        onCurrentFrameChanged={handleCurrentTimeChange}
        videoElement={videoElement}
        onFullScreenToggle={onFullScreenToggle}
        videoMetadata={videoMetadata}
      />
    );
  },
);
